import {
  LinkingOptions,
  PathConfigMap,
} from '@react-navigation/native';
import * as Linking from 'expo-linking';
import { Platform } from 'react-native';

import { IS_WEB } from '@utils';
import { screens } from './screens';
import config from '@config';
import {
  DeepLinkingService,
  NotificationsService,
  AnalyticsService,
  BrazeService,
} from '@services';
import type { ChatStackParamListType } from './types';

const NO_PATH = undefined;
// TODO: may should move MOBILE_SCHEME const into config variables
const MOBILE_SCHEME = 'syntho://';
const prefix = IS_WEB ? Linking.createURL('/') : config.APP_URL;

function trackNotificationAnalytic(title?: string) {
  if (title) {
    AnalyticsService.trackNotification(title);
  }
}

function createUserGroupPathConfigMap(
  basePath = '',
): PathConfigMap<any> {
  return {
    [screens.UserProfile]: {
      path: `${basePath}/user/:id`,
      parse: {
        id: (id) => Number(id),
      },
    },
    [screens.Followers]: {
      path: `${basePath}/followers/:id`,
      parse: {
        id: (id) => Number(id),
      },
    },
    [screens.Following]: {
      path: `${basePath}/following/:id`,
      parse: {
        id: (id) => Number(id),
      },
    },
  };
}

export const linking: LinkingOptions<ReactNavigation.RootParamList> =
  {
    prefixes: [prefix, MOBILE_SCHEME],
    config: {
      initialRouteName: screens.SwitchNavigator,
      screens: {
        [screens.SwitchNavigator]: {
          path: NO_PATH,
          screens: {
            [screens.Initial]: {
              path: NO_PATH,
            },
            [screens.AuthNavigator]: {
              path: 'auth',
              screens: {
                [screens.SignIn]: 'sign-in',
                [screens.SignUp]: 'sign-up',
                [screens.ForgotPassword]: 'forgot-password',
                [screens.SetNewPassword]: 'set-new-password',
                [screens.ChooseSubscriptionPlan]: 'subscription',
                [screens.Referral]: 'referral',
                [screens.AddPaymentMethod]: {
                  path: 'add-payment-method',
                  parse: {
                    tariffPlan: (tariffPlan: string | number) =>
                      Number(tariffPlan),
                  },
                },
              },
            },
            [screens.MainNavigator]: {
              path: NO_PATH,
              screens: {
                [screens.TabNavigator]: {
                  path: NO_PATH,
                  screens: {
                    [screens.HomeTabNavigator]: {
                      path: 'home',
                      screens: {
                        [screens.News]: 'news',
                        [screens.Latest]: '',
                        [screens.MyHub]: 'my-hub',
                      },
                    },
                    [screens.MyMusic]: 'my-music',
                    // [screens.Search]: 'search',
                    [screens.Hub]: 'hub',
                    [screens.Profile]: 'profile',
                    [screens.TrackFeedback]: 'track-feedback',
                  },
                },
                [screens.PersonalizationNavigator]: {
                  path: NO_PATH,
                  screens: {
                    [screens.SuccessfulSignIn]: 'successful-sign-in',
                    [screens.Questionnaire]: 'questionnaire',
                    [screens.InviteSuggestion]: 'invite-suggestion',
                    [screens.SkillLevelQuestionnaire]:
                      'skill-level-questionnaire',
                    // Hide the "Users Suggestion" screen for now
                    // [screens.UsersSuggestion]: 'users-suggestion',
                    // ...createUserGroupPathConfigMap(
                    //   'users-suggestion',
                    // ),
                  },
                },
                [screens.SearchTabNavigator]: {
                  path: 'search',
                  screens: {
                    [screens.SearchTutorial]: 'tutorial',
                    [screens.SearchProfile]: 'profile',
                  },
                },
                [screens.HubNavigator]: {
                  path: NO_PATH,
                  screens: {
                    [screens.Tutorial]: {
                      path: 'tutorial/:id',
                      parse: {
                        id: (id: string | number) => Number(id),
                        courseId: (id: string | number) => Number(id),
                        extraCategoryId: (id: string | number) =>
                          Number(id),
                      },
                    },
                    [screens.CourseSummary]: {
                      path: 'course-summary/:id',
                      parse: {
                        id: (id: string | number) => Number(id),
                      },
                    },
                  },
                },
                [screens.ProfileNavigator]: {
                  path: NO_PATH,
                  screens: {
                    [screens.UpdatePassword]: 'update-password',
                    [screens.Settings]: 'settings',
                    [screens.Notifications]: 'notifications',
                    [screens.Subscription]: 'subscription',
                    [screens.Language]: 'language',
                    [screens.Security]: 'security',
                    [screens.InviteFriend]: 'invite-friend',
                    [screens.Help]: 'help',
                    [screens.ContactUs]: 'contact-us',
                    [screens.About]: 'about',
                    [screens.CreditCards]: 'subscription/cards',
                    [screens.UpdateCreditCard]: {
                      path: 'subscription/cards/:id',
                      parse: {
                        id: (id: string | number) => Number(id),
                      },
                    },
                  },
                },
                [screens.ChatNavigator]: {
                  path: 'chats',
                  screens: {
                    ...Platform.select<
                      PathConfigMap<ChatStackParamListType>
                    >({
                      web: {
                        [screens.Chats]: {
                          path: ':conversationId?',
                          parse: {
                            conversationId: (id) => Number(id),
                          },
                        },
                      },
                      default: {
                        [screens.Chats]: {
                          path: NO_PATH,
                          parse: {
                            conversationId: (id) => Number(id),
                          },
                        },
                        [screens.Messaging]: {
                          path: ':conversationId',
                          parse: {
                            conversationId: (id) => Number(id),
                          },
                        },
                      },
                    }),
                    [screens.FindFriend]: 'find-friend',
                  },
                },
                [screens.FeedbackNavigator]: {
                  path: NO_PATH,
                  screens: {
                    [screens.CreditsPayment]: 'purchase-credits',
                  },
                },
                ...createUserGroupPathConfigMap(),
              },
            },
          },
        },
        [screens.PaymentConfirm]: {
          path: 'payment/confirm',
          parse: {
            type: (type: string) => Number(type),
            tariffPlan: (tariffPlan: string) => Number(tariffPlan),
          },
        },
      },
    },

    async getInitialURL() {
      // First, you would need to get the initial URL from your third-party integration
      // The exact usage depend on the third-party SDK you use
      // For example, to get to get the initial URL for Firebase Dynamic Links:
      const initialLink = await DeepLinkingService.getInitialLink();

      if (initialLink) {
        return initialLink.url;
      }

      // When launching an iOS application that has previously been force closed,
      // React Native's Linking API doesn't support handling deep links embedded in push
      // notifications. This is due to a race condition on startup between the native call
      // to RCTLinkingManager and React's loading of its JavaScript. This function provides
      // a workaround: If an application is launched from a push notification click, we
      // return any Braze deep links in the push payload.
      const iosPushInitialUrl = await BrazeService.getInitialURL();

      if (iosPushInitialUrl) {
        return iosPushInitialUrl;
      }

      // Check if app was opened from a deep link
      const url = await Linking.getInitialURL();

      if (url !== null) {
        return url;
      }

      const message =
        await NotificationsService.getInitialNotification();

      if (message) {
        trackNotificationAnalytic(message.notification?.title);

        return message.data.url;
      }

      return null;
    },

    subscribe(listener) {
      const onReceiveUrl = ({ url }: { url: string }) =>
        listener(url);

      // Listen to incoming links from deep linking
      Linking.addEventListener('url', onReceiveUrl);

      // Listen to incoming links from Firebase Dynamic Links
      const unsubscribeDynamicLinks = DeepLinkingService.onLink(
        (link) => {
          listener(link.url);
        },
      );

      // Listen to Firebase Push Notifications
      const unsubscribeNotification =
        NotificationsService.onNotificationOpenedApp((message) => {
          if (message.data.url) {
            // Any custom logic to check whether the URL needs to be handled
            // Call the listener to let React Navigation handle the URL
            trackNotificationAnalytic(message.notification?.title);

            listener(message.data.url);
          }
        });

      return () => {
        // Clean up the event listeners
        unsubscribeNotification();
        unsubscribeDynamicLinks();
      };
    },
  };
